import { useEffect, useState } from 'react';
import type { ClientApi } from '@openclassrooms/sdk-web';
import type { CompletionState, EmbeddedChapter } from '../types';
import { fetchChapterData } from '../services/chapterDataFetcher';
import { processChapterData } from '../services/chapterDataProcessor';
interface EmbeddedChapterDataResult {
  isLoading: boolean;
  chapterContent: string;
  hasFailedLoading: boolean;
  completionState: CompletionState;
  setCompletionState: React.Dispatch<React.SetStateAction<CompletionState>>;
  chapterTitle: string;
  isAlreadyCompletedByUser: boolean;
  setIsAlreadyCompletedByUser: React.Dispatch<React.SetStateAction<boolean>>;
  previousEmbeddedCompletionById: Record<string, boolean>;
}
interface EmbeddedChapterDataParams {
  client: ClientApi;
  href: string;
  linkLabel: string;
  courseId: number;
  chapterId: number;
  previousEmbeddedChapters: EmbeddedChapter[];
}

/**
 * useEmbeddedChapterData
 *
 * Fetches chapter content + completion status for this chapter and all other embedded chapters on the page.
 *
 * @param client - SDK API client
 * @param href - chapter URL (for title slug fallback if metadata title missing)
 * @param linkLabel - chapter label (for title fallback if metadata title missing)
 * @param courseId - current course ID
 * @param chapterId - chapter ID to fetch
 * @param previousEmbeddedChapters - other embedded chapters on the page; used to implement guided learning
 */
const useEmbeddedChapterData = ({
  client,
  href,
  linkLabel,
  courseId,
  chapterId,
  previousEmbeddedChapters
}: EmbeddedChapterDataParams): EmbeddedChapterDataResult => {
  const [isLoading, setIsLoading] = useState(true);
  const [chapterContent, setChapterContent] = useState('');
  const [hasFailedLoading, setHasFailedLoading] = useState(false);
  const [completionState, setCompletionState] = useState<CompletionState>('idle');
  const [chapterTitle, setChapterTitle] = useState('');
  const [isAlreadyCompletedByUser, setIsAlreadyCompletedByUser] = useState(false);
  const [previousEmbeddedCompletionById, setPreviousEmbeddedCompletionById] = useState<Record<string, boolean>>({});
  useEffect(() => {
    let isCancelled = false;
    const loadChapterData = async () => {
      try {
        const rawData = await fetchChapterData(client, courseId, chapterId, previousEmbeddedChapters);
        if (isCancelled) {
          return;
        }
        const {
          metadata,
          content,
          previousEmbeddedChapters: previousEmbeddedRawResults
        } = rawData;
        const processed = processChapterData({
          metadata,
          content,
          href,
          linkLabel,
          previousEmbeddedRawResults
        });
        setChapterTitle(processed.title);
        setChapterContent(processed.content);
        setIsAlreadyCompletedByUser(processed.isCompleted);
        setPreviousEmbeddedCompletionById(processed.previousEmbeddedCompletionById);
        if (processed.isCompleted) {
          setCompletionState('completed');
        }
        if (!processed.content) {
          setHasFailedLoading(true);
        }
      } catch {
        if (!isCancelled) {
          setChapterTitle(linkLabel);
          setHasFailedLoading(true);
        }
      } finally {
        if (!isCancelled) {
          setIsLoading(false);
        }
      }
    };
    loadChapterData();
    return () => {
      isCancelled = true;
    };
  }, [chapterId, client, courseId, href, linkLabel, previousEmbeddedChapters]);
  return {
    isLoading,
    chapterContent,
    hasFailedLoading,
    completionState,
    setCompletionState,
    chapterTitle,
    isAlreadyCompletedByUser,
    setIsAlreadyCompletedByUser,
    previousEmbeddedCompletionById
  };
};
export default useEmbeddedChapterData;